import { Button, Grid, Paper, Skeleton, Stack, Table, TableBody, TableContainer, TableHead, List, ListSubheader } from '@mui/material';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';
import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FormDescriptionTypography, FormHeaderPaper, FormRowGroupTableRow, FormTableCell, FormTableRow, FormTitleTypography, SectorTitleTypography, DataEntryTextField } from '../../../components/CustomStyles/StyledComponents';
import toastr from "toastr";
import { toastrCustomOptions, toastrCustomOptionsLonger } from "../../../constants/toastrOptions";
import _ from "lodash";
import { isEqual } from "lodash";
import { addCommas, removeCommas } from '../../../helpers/stringFormatHelper';
import {produce} from 'immer';
import {
    getAllocateRenewableHeat,
    getRenewableHeatFormData,
    getRenewableHeatMetaData,
    resetResponseState,
    saveRenewableHeatData,
    setLoading,
    setRenewableHeatFormData,
    setRenewableHeatWholeFarmData,
    unsetLoading,
    fullResetRenewableHeat
} from '../../../store/appAction';
import DataEntryButtonStack from '../../../components/Buttons/DataEntryButtonStack';
import SelectedFarm from '../../../components/SelectedFarm/SelectedFarm';
import SelectedReport from '../../../components/SelectedReport/SelectedReport';
import store from '../../../store';

const CELL_WIDTH = {
    RowTitle: "20%",
    BiogasHeat: "13%",
    GrassOrStraw: "13%",
    HeatTotalProduced: "13%",
    HeatTotalUsed: "13%",
    LogsOrChips: "13%",
    WoodPellets: "13%",
    GeneralCell: "13%"
};

const DataEntryTable = ({ sector, initialFormData, colGroups }) => {
    toastr.options = toastrCustomOptions;
    const dispatch = useDispatch();
    const [sectorDataInt, setSectorDataInt] = useState(undefined);
    const [sectorDataExt, setSectorDataExt] = useState(undefined);

    useEffect(() => {
        setSectorDataInt(initialFormData);
        setSectorDataExt(initialFormData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFormData]);

    useEffect(() => {
        if (sectorDataExt && !_.isEmpty(sectorDataExt)) {
            dispatch(setRenewableHeatFormData({
                sectorId: sector.id,
                formData: sectorDataExt
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectorDataExt]);

    const handleInputChange = useCallback((e, rowId, colId, rule) => {
        let value = removeCommas(e.target.value);
        if (e.target.inputMode === "numeric") {
            let numericRegex = /^-?[0-9]*(\.[0-9]{0,2})?$/;
            if (numericRegex.test(value)) {
                if (rule.max < parseFloat(value || 0)) {
                    toastr.warning(`This input only accepts values between ${addCommas(rule.min
                    )} and ${addCommas(rule.max)}.  please enter the value again.`,
                        undefined,
                        toastrCustomOptionsLonger
                    );
                } else {
                    setSectorDataInt(produce((draft) => {
                        draft[rowId][colId] = value;
                    }));
                    setSectorDataExt(produce((draft) => {
                        draft[rowId][colId] = parseFloat(value);
                    }));
                }
            }
        } else {
            setSectorDataInt(produce((draft) => {
                draft[rowId][colId] = value;
            }));
            setSectorDataExt(produce((draft) => {
                draft[rowId][colId] = parseFloat(value);
            }));
        }
    }, []);

    const getCellContent = ({ rowId, cell, rowData }) => {
        switch (cell.dataType) {
            case "TextField":
                return (
                    <DataEntryTextField
                        size="small"
                        variant="outlined"
                        inputProps={{ inputMode: "numeric" }}
                        style={{ width: 125, textAlign: "center" }}
                        value={(rowData && addCommas(rowData[cell.mappingProperty])) || ''}
                        onChange={(e) =>
                            handleInputChange(
                                e,
                                rowId,
                                cell.mappingProperty,
                                cell.validations
                            )}
                    />
                );
            default:
                return <FormTableCell sx={{ width: "12%" }}></FormTableCell>;
        }
    };

    return (
        <>
            {sector ?
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <SectorTitleTypography>{sector.title}</SectorTitleTypography>
                    <Fragment>
                        <Table>
                            {/* The following code segment has been commented out due to the requirement of AGC-977 */}
                            {/* <TableHead>
                                <FormRowGroupTableRow>
                                    <FormTableCell sx={{ width: CELL_WIDTH['RowTitle'] }} ></FormTableCell>
                                    {colGroups && colGroups.map((colGroup) =>
                                        <FormTableCell
                                            align="center"
                                            colSpan={colGroup.length}
                                            style={{ fontSize: '1rem' }}
                                        >
                                            {!colGroup.title.includes("Empty") && colGroup.title}
                                        </FormTableCell>
                                    )}
                                </FormRowGroupTableRow>
                                <FormTableRow>
                                    <FormTableCell sx={{ width: CELL_WIDTH['RowTitle'] }} ></FormTableCell>
                                    {(sector.columns || {}).map((column) => (
                                        <FormTableCell sx={{ width: CELL_WIDTH[column.cssClass] }} >
                                            {column.title}
                                        </FormTableCell>
                                    ))}
                                </FormTableRow>
                            </TableHead> */}
                            <TableBody>
                                {sector.rows.map((row) => (
                                    <FormTableRow>
                                        <FormTableCell sx={{ width: CELL_WIDTH['RowTitle'] }} >
                                            {row.title}
                                        </FormTableCell>
                                        {row.cells.map((cell) => (
                                            <FormTableCell sx={{ width: CELL_WIDTH[cell.cssClass] ? CELL_WIDTH[cell.cssClass] : CELL_WIDTH.GeneralCell }} >
                                                {sectorDataInt && cell.visible && getCellContent({
                                                    rowId: row.id,
                                                    cell: cell,
                                                    rowData: sectorDataInt && sectorDataInt[row.id]
                                                })}
                                            </FormTableCell>
                                        ))}

                                    </FormTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Fragment>
                </TableContainer>
                : <Skeleton variant="rectangular" width={'100%'} height={150} />
            }
        </>
    )
};

const RenewableHeat = (props) => {
    const dispatch = useDispatch();
    let navigate = useNavigate();

    const reportId = useSelector(state => state.common.reportId);
    const metaData = useSelector(state => state.renewableHeat.metaData);
    const initialWholeFarmData = useSelector(state => state.renewableHeat.initialWholeFarmData);
    const currentWholeFarmData = useSelector(state => state.renewableHeat.currentWholeFarmData);
    const initialFormData = useSelector(state => state.renewableHeat.initialFormData);
    const { successMsg, errorMsg } = useSelector(state => state.common);
    const { reportName, locked } = useSelector(state => state.farmReport.reportSettings);

    const [formData, setFormData] = useState({})
    const [modalOpen, setModalOpen] = useState(false);
    const [nextClicked, setNextClicked] = useState(false);
    const [wholeFarmDataInt, setWholeFarmDataInt] = useState(undefined);
    const [wholeFarmDataExt, setWholeFarmDataExt] = useState(undefined);
    const [colGroups, setColGroups] = useState([]);

    // ToDo get rid of this and the duplication of initial and current data sets
    const [usedAllocated, setUsedAllocated] = useState(false);
    

    const sectors = metaData?.form?.sectors || [];
    const wholeFarm = metaData?.form?.wholeFarm || [];
    const columns = metaData?.form?.columns || [];

    useEffect(() => {
        dispatch(fullResetRenewableHeat());
        dispatch(setLoading());
        dispatch(getRenewableHeatMetaData({ reportId }));
        dispatch(getRenewableHeatFormData({ reportId }));
        dispatch(resetResponseState());
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => {
            dispatch(fullResetRenewableHeat());
        }
    }, []);

    useEffect(() => {
        const tempData = { ...initialFormData };
        setFormData(tempData);
        setTimeout(() => {
            dispatch(unsetLoading());
        }, 500);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFormData]);

    useEffect(() => {
        setWholeFarmDataInt({ ...initialWholeFarmData });
        setWholeFarmDataExt({ ...initialWholeFarmData });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialWholeFarmData]);

    useEffect(() => {
        if (successMsg) {
            dispatch(unsetLoading());
            toastr.success(successMsg);
            dispatch(resetResponseState());
            if (nextClicked) {
                dispatch(fullResetRenewableHeat());
                navigate('/data-entry/energy-waste/waste-transport-water');
            }else{
                dispatch(getRenewableHeatFormData({ reportId }));
            }
            setNextClicked(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [successMsg]);

    useEffect(() => {
        if (errorMsg) {
            dispatch(unsetLoading());
            toastr.error(errorMsg, undefined, toastrCustomOptionsLonger);
            dispatch(resetResponseState());
            dispatch(getRenewableHeatFormData({ reportId }));
            setNextClicked(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorMsg]);

    useEffect(() => {
        let groupedList = _(columns ? columns : [])
            .groupBy("group")
            .map((items, Group) => ({
                title: Group,
                length: items.length,
            }))
            .value();
        setColGroups(groupedList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns]);

    useEffect(() => {
        if (wholeFarmDataExt && !_.isEmpty(wholeFarmDataExt)) {
            dispatch(setRenewableHeatWholeFarmData({
                wholeFarmData: wholeFarmDataExt
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wholeFarmDataExt]);

    const handleModalResponse = (positiveResponse) => {
        setModalOpen(false);
        if (positiveResponse) {
            // window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            dispatch(getRenewableHeatMetaData({ reportId }));
            dispatch(getRenewableHeatFormData({ reportId }));
        }
    }

    const isFormUpdated = () => {
        const renewableHeatData = store.getState().renewableHeat;
        const isUpdatedWholeFarmData = !isEqual(renewableHeatData?.initialWholeFarmData, renewableHeatData?.currentWholeFarmData);
        const isUpdatedEnterpriseData = !isEqual(renewableHeatData?.initialFormData, renewableHeatData?.currentFormData);
        return (isUpdatedWholeFarmData || isUpdatedEnterpriseData || usedAllocated)
    }

    const doSave = () => {
        dispatch(setLoading());
        dispatch(saveRenewableHeatData({ reportId }));
        setUsedAllocated(false)
    }

    const handleSaveClick = (e) => {
        if(!isFormUpdated()){
            return;
        }
        doSave();
    }

    const handleNextClick = (e) => {
        setNextClicked(true);
        if (locked || !isFormUpdated())
            navigate('/data-entry/energy-waste/waste-transport-water');
        else {
            doSave();
        };
    }

    const handleAllocateRenewableHeatClick = () => {
        if (currentWholeFarmData && reportId){
            dispatch(getAllocateRenewableHeat({ reportId, currentWholeFarmData }));
            setUsedAllocated(true);
        }
    }

    const handleInputChange = useCallback((e, colId, rule) => {
        let value = removeCommas(e.target.value);
        if (e.target.inputMode === "numeric") {
            let numericRegex = /^-?[0-9]*(\.[0-9]{0,2})?$/;
            if (numericRegex.test(value)) {
                if (rule.max < parseFloat(value || 0)) {
                    toastr.warning(`This input only accepts values between ${addCommas(rule.min
                    )} and ${addCommas(rule.max)}.  please enter the value again.`,
                        undefined,
                        toastrCustomOptionsLonger
                    );
                } else {
                    setWholeFarmDataInt(produce((draft) => {
                        draft[colId] = value;
                        let tempRowData = { ...draft };
                        delete tempRowData['HeatTotalProduced'];
                        delete tempRowData['HeatTotalUsed'];
                        let rowValues = Object.values(tempRowData);
                        const totalValue = rowValues.reduce((x, y) => parseFloat(x || 0) + parseFloat(y || 0))
                        draft['HeatTotalProduced'] = totalValue || '';
                    }));
                    setWholeFarmDataExt(produce((draft) => {
                        draft[colId] = parseFloat(value);
                        let tempRowData = { ...draft };
                        delete tempRowData['HeatTotalProduced'];
                        delete tempRowData['HeatTotalUsed'];
                        let rowValues = Object.values(tempRowData);
                        const totalValue = rowValues.reduce((x, y) => parseFloat(x || 0) + parseFloat(y || 0))
                        draft['HeatTotalProduced'] = parseFloat(totalValue);
                    }));
                }
            }
        } else {
            setWholeFarmDataInt(produce((draft) => {
                draft[colId] = value;
            }));
            setWholeFarmDataExt(produce((draft) => {
                draft[colId] = parseFloat(value);
            }));
        }
    }, []);

    return (
        <ScrollSync>
        <Grid container spacing={2} className={locked && 'deactivated'}>
            <Grid item xs={12} md={12} lg={12}>
                <FormHeaderPaper sx={{ p: 2, display: "flex", flexDirection: "column" }} divider={false}>
                    <SelectedFarm />
                    <SelectedReport reportName={reportName} />
                    <FormTitleTypography variant="h5" component="div">
                        Renewable Heat
                    </FormTitleTypography>
                    <FormDescriptionTypography variant="body2" paragraph>
                        Enter quantities of heat produced and used on-farm from various renewable sources.
                    </FormDescriptionTypography>
                </FormHeaderPaper>
            </Grid>
            <Grid item xs={12} sx={{ paddingTop:'0 !important' }}>
                <Paper sx={{ p: 2, display: "flex", flexDirection: "column", paddingTop:'0' }}>
                    <List sx={{ mb: 2 }}>
                        <ListSubheader sx={{ bgcolor: "background.paper" }}>
                            <ScrollSyncPane>
                                <>
                                <DataEntryButtonStack
                                    modalOpen={modalOpen}
                                    setModalOpen={setModalOpen}
                                    handleModalResponse={handleModalResponse}
                                    handleSaveClick={handleSaveClick}
                                    handleNextClick={handleNextClick}
                                />
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <FormRowGroupTableRow>
                                                <FormTableCell></FormTableCell>
                                                {colGroups && colGroups.map((colGroup) =>
                                                    <FormTableCell
                                                        align="center"
                                                        colSpan={colGroup.length}
                                                        style={{ fontSize: '1rem' }}
                                                    >
                                                        {!colGroup.title.includes("Empty") && colGroup.title}
                                                    </FormTableCell>
                                                )}
                                            </FormRowGroupTableRow>
                                            <FormTableRow>
                                                <FormTableCell sx={{ width: CELL_WIDTH['RowTitle'] }} ></FormTableCell>
                                                {Object.values(wholeFarm || {}).map((column) => (
                                                    <FormTableCell sx={{ width: CELL_WIDTH[column.cssClass] }} >
                                                        {column.title}
                                                    </FormTableCell>
                                                ))}
                                            </FormTableRow>
                                        </TableHead>
                                    </Table>
                                </TableContainer>
                                </>
                            </ScrollSyncPane>
                        </ListSubheader>
                        {(wholeFarmDataInt && wholeFarm) && <> <TableContainer component={Paper} sx={{ mb: 3 }}>
                            <SectorTitleTypography>Whole Farm</SectorTitleTypography>
                            <Table>
                                <TableBody>
                                    <FormTableRow>
                                        <FormTableCell sx={{ width: CELL_WIDTH['RowTitle'] }}>Whole Farm</FormTableCell>
                                        {Object.values(wholeFarm || {}).map((cell,index) => (
                                            <FormTableCell>
                                                <DataEntryTextField
                                                    size="small"
                                                    variant="outlined"
                                                    inputProps={{ inputMode: "numeric" }}
                                                    style={{ width: 125, textAlign: "center" }}
                                                    disabled={cell.mappingProperty === 'HeatTotalProduced'}
                                                    value={wholeFarmDataInt && addCommas(wholeFarmDataInt[cell.mappingProperty])}
                                                    onChange={(e) => handleInputChange(e, cell.mappingProperty, cell.validations)}
                                                    autoFocus={locked === false &&  index === 0}
                                                />
                                            </FormTableCell>
                                        ))}
                                    </FormTableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={3}>
                                <Button variant="contained" align="left" sx={{ textTransform: 'none', mb: 2 }}
                                    onClick={() => handleAllocateRenewableHeatClick()}> Allocate renewable heat
                                </Button>
                            </Stack>
                        </>}
                        {formData && sectors.map((sector, index) => (
                            <><React.Fragment key={index}>
                                <DataEntryTable
                                    key={sector.id}
                                    sector={sector}
                                    initialFormData={formData[sector.id] ? formData[sector.id] : {}}
                                    colGroups={colGroups}
                                />
                            </React.Fragment></>
                        ))}
                    </List>
                </Paper>
            </Grid>
        </Grid >
        </ScrollSync>
    )
}

export default RenewableHeat